/**
 * Godhood Level, read off the Chronicle.
 *
 * `D §6`: **A GOD LEVELS WHEN THE WORLD MEANINGFULLY CHANGES THROUGH THEM.**
 * Progression follows ACT → CONSEQUENCE → PERSISTENCE → REVELATION, and XP is
 * explicitly not awarded for button presses, kills, followers, or repetition.
 * `GodhoodWeb.level` has been a plain field nothing writes since the Web was
 * built, because nothing in the simulation could tell those apart. The
 * Chronicle can, so this is the read.
 *
 * ## What is counted, and what is refused
 *
 * The unit is an **enduring strand** — a relationship with one subject that
 * the world confirmed changed. Not an act. `Chronicle` already refuses to let
 * repetition confirm anything (law 25), so five hundred casts on bare ground
 * arrive here as nothing at all, and one tended tree arrives as one.
 *
 * Three multipliers, and each of them is a law rather than a knob:
 *
 *  · **Novelty.** The k-th enduring strand of the same *kind* — same verb,
 *    same way of enduring — is worth `NOVELTY_DECAY^(k-1)`. Law 25 says "the
 *    same act on the same subject in the same conditions must approach zero";
 *    the fold already collapses same-*subject* repetition to one row, and this
 *    is the same rule one level up. Promoting your fortieth tree teaches a god
 *    almost nothing about being a god.
 *  · **Persistence.** A strand is worth more the longer it has stood since the
 *    world confirmed it, saturating logarithmically. This is `D §6`'s third
 *    layer and law 33's "the reward for making something important is more
 *    history": a thing that lasted keeps paying while it lasts.
 *  · **Breadth.** Nothing extra is added for it — breadth arrives for free out
 *    of novelty, because a god who only ever does one thing runs into the
 *    decay and a god who does five things does not. Law 26 wants asymmetry
 *    between paths to be a *feature*, so nothing here pushes toward breadth;
 *    it merely stops rewarding sameness.
 *
 * **Failure counts** (law 25, `D §6`). A Weaken that blasts a tree into
 * significance endures exactly as a Strengthen that tends one into it — the
 * Chronicle records `thing_promoted` either way, and nothing here reads the
 * sign. A god learns from what the world remembers, not from whether it liked
 * the outcome.
 *
 * ## What is deliberately absent
 *
 * No follower term (law 18: progression must never reduce to follower count),
 * no act count, no time-played term. `Tally.acts` and `Tally.retiredActs` are
 * carried through to the read-out so a god can be *told* what came to nothing
 * — that sentence is the point of keeping them — but they are never a
 * numerator.
 *
 * Pure by law 7: no three, no `Math.random`, no clock. `now` is passed in.
 */

import type { Chronicle, Strand } from '@/sim/chronicle';
import { POINTS_AT_LEVEL_100 } from './web';

const YEAR = 365 * 86400;

/**
 * How fast a repeated kind of achievement stops teaching anything.
 *
 * At 0.72 the tenth identical endurance is worth **5.2%** of the first and the
 * twentieth **0.2%**, and the whole infinite series sums to **3.57** strands'
 * worth. That last number is the important one: it means a single strategy
 * repeated for ever is worth 3.57 however long it is repeated, which is law
 * 25's "must approach zero" with an actual number on it, and a god that only
 * ever does one thing cannot pass level 14 on it. `tools/level.ts` prints the
 * whole series.
 */
export const NOVELTY_DECAY = 0.72;

/**
 * The scale on which standing keeps growing, in world-years. A strand is worth
 * `1 + ln(1 + age/PERSISTENCE_SCALE)`, so it doubles at 1.72 scales and never
 * runs away: a century-old consequence is worth 3.40× a fresh one, not a
 * hundred times.
 */
export const PERSISTENCE_SCALE = 10;

/**
 * Standing at which a deity reaches level 100 — `D §6.2`'s top of the track.
 *
 * Derived, not chosen, and the derivation is the interesting part. Because
 * novelty decays geometrically *inside* a kind, standing does not grow with
 * time or with effort at all. It is exactly
 *
 *     kinds × 3.57 × persistence(age)
 *
 * and nothing else — so the top of the track is a claim about **breadth ×
 * time**, never about volume. 150 is *fifteen kinds of consequence, kept for
 * fifty world-years*: 15 × 3.57 × (1 + ln 6) = 149.6.
 *
 * Fifteen is not arbitrary either. `tools/level.ts` enumerates what the build
 * can actually confirm. It was ten (verb, endurance) pairs from the day
 * `fire_burned` began being emitted (`ROADMAP.md` §28) — the tenth being
 * `strike|burn+fire`, a bolt whose fire went on to burn enough ground to be
 * worth remembering (not `strike|burn`: nothing burns without having caught,
 * so the fire that *took* carries both and is a different kind from the fire
 * that merely started) — and it became fifteen on 2026-08-22, when the two
 * verbs with no horizon C were given one: `omen|answer`, a sign somebody
 * walked to, and `becoming` for every verb that can press a life over a line
 * the card speaks at (`strengthen`, `weaken`, `rain`, `strike`). So the top of
 * the track is *everything the world can remember you for, held for a
 * lifetime*, which is what `D §6.2`'s ceiling ought to mean — and it moved
 * from 100 to 150 because there is more to be remembered for, not because
 * the track got longer.
 *
 * That the derivation and the enumeration agree is a coincidence worth
 * distrusting rather than admiring: the day a sixteenth kind is emitted this
 * number is a claim about fifteen sixteenths of the world instead of all of
 * it, and `test/level.ts` asserts the two against each other so the drift is
 * loud. Re-derive it; do not leave it.
 */
export const STANDING_AT_100 = 150;

/** How sharply the level track bends. Below 1, early levels come faster. */
export const LEVEL_EXPONENT = 0.6;

/** What one enduring strand contributed, and why. */
export interface Contribution {
  strand: Strand;
  /** `${verb}|${how it endured}` — what makes two achievements the same kind. */
  kind: string;
  /** 1 for the first of its kind, decaying for each after it. */
  novelty: number;
  /** How much its standing the world has had time to confirm. */
  persistence: number;
  value: number;
}

/**
 * A deity's whole progression, as a reading rather than a number. The
 * contributions are ordered exactly as they were earned, which is what lets a
 * panel say *this* is what you became and in what order.
 */
export interface Standing {
  level: number;
  /** Points the level grants — `D §6.2`, and what the Web spends. */
  points: number;
  total: number;
  contributions: Contribution[];
  /** Acts of any verb. Reported, never a numerator (law 25). */
  acts: number;
  /** Acts inside strands the journal forgot — what came to nothing. */
  barrenActs: number;
}

/** `D §6.2`: levels 1–100, saturating. There is no prestige reset. */
export const levelFor = (standing: number): number => {
  if (!(standing > 0)) return 1;
  const f = Math.pow(Math.min(1, standing / STANDING_AT_100), LEVEL_EXPONENT);
  return Math.max(1, Math.min(100, 1 + Math.floor((POINTS_AT_LEVEL_100 - 1) * f)));
};

/**
 * What a deity has become, read off what the world remembers of it.
 *
 * Strands are taken in the order the world confirmed them (`enduredAt`), with
 * `key` breaking ties, so the reading is a pure function of history and not of
 * map iteration order — two identical worlds must produce identical gods.
 */
export function standingOf(c: Chronicle, godId: string, now: number): Standing {
  const enduring = c.enduringOf(godId)
    .sort((a, b) => (a.enduredAt! - b.enduredAt!) || (a.key < b.key ? -1 : 1));

  const seen = new Map<string, number>();
  const contributions: Contribution[] = [];
  let total = 0;
  for (const s of enduring) {
    const kind = `${s.verb}|${[...s.left].sort().join('+')}`;
    const k = seen.get(kind) ?? 0;
    seen.set(kind, k + 1);
    const novelty = Math.pow(NOVELTY_DECAY, k);
    /**
     * …counted only for as long as the world went on confirming it.
     *
     * `persistence` GROWS with age, so a strand whose subject has lapsed —
     * a promoted tree decayed back to an ordinary tree — used to go on paying
     * a god more every year for something the world had forgotten. Freezing
     * the clock at `endedAt` is the honest reading rather than deleting the
     * contribution: the deed *did* endure, for exactly that long, and it stops
     * being rewarded for continuing because it stopped continuing.
     *
     * Law 33 is the reason it is frozen and not erased: the reward for making
     * something important is more history, never less, and a thing that mattered
     * for eleven years mattered for eleven years.
     */
    const until = Math.min(now, s.endedAt ?? now);
    const age = Math.max(0, until - s.enduredAt!) / YEAR;
    const persistence = 1 + Math.log(1 + age / PERSISTENCE_SCALE);
    const value = novelty * persistence;
    total += value;
    contributions.push({ strand: s, kind, novelty, persistence, value });
  }

  const tally = c.tallyOf(godId);
  const level = levelFor(total);
  return {
    level,
    points: level,
    total,
    contributions,
    acts: tally.acts,
    barrenActs: tally.retiredActs,
  };
}
